/**
 * Higienização do HTML de campo clínico, do lado da tela.
 *
 * Espelha `common/helpers/clinical-html.helper.ts` do servidor. Existir nos dois
 * lados é deliberado: o servidor é o controle — ele higieniza o que grava e o
 * que devolve —, e aqui a mesma allowlist protege o que o editor cola e o que a
 * tela renderiza, sem depender de uma ida ao servidor para mostrar o que a
 * pessoa acabou de digitar.
 *
 * **Allowlist, nunca blocklist.** Tag fora da lista some e o texto dentro dela
 * fica; atributo nenhum sobrevive, o que dispensa analisar `href`, `src`,
 * `style` ou `on*`.
 */

/** As tags que um registro clínico precisa. Nada além disto sobrevive. */
const TAGS_PERMITIDAS = new Set([
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
]);

/**
 * Blocos que viram parágrafo em vez de sumir.
 *
 * `contenteditable` do navegador envolve cada linha nova num `<div>`. Descartar
 * a tag preservaria o texto mas **perderia a quebra de bloco** — duas linhas de
 * anamnese virariam uma só, coladas.
 */
const BLOCOS_COMO_PARAGRAFO = new Set(['div', 'section', 'article']);

/** Elementos cujo conteúdo também é descartado, não só a tag. */
const TAGS_COM_CONTEUDO_DESCARTADO = ['script', 'style', 'iframe', 'object', 'embed', 'template'];

/** Reduz o HTML à lista de tags permitidas, sem nenhum atributo. */
export function sanitizeClinicalHtml(html: string): string {
  let limpo = html;

  for (const tag of TAGS_COM_CONTEUDO_DESCARTADO) {
    limpo = limpo.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?</${tag}\\s*>`, 'gi'), '');
    limpo = limpo.replace(new RegExp(`<${tag}\\b[^>]*/?>`, 'gi'), '');
  }

  limpo = limpo.replace(/<!--[\s\S]*?-->/g, '');

  limpo = limpo.replace(
    /<\s*(\/)?\s*([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/)?\s*>/g,
    (_tudo, fecha: string | undefined, nome: string) => {
      let tag = nome.toLowerCase();
      if (BLOCOS_COMO_PARAGRAFO.has(tag)) tag = 'p';
      if (!TAGS_PERMITIDAS.has(tag)) return '';

      if (tag === 'br') return '<br />';
      return fecha ? `</${tag}>` : `<${tag}>`;
    },
  );

  limpo = limpo.replace(/<(?![a-zA-Z/])/g, '&lt;');

  return ehVazio(limpo) ? '' : limpo.trim();
}

/**
 * `true` quando o conteúdo não tem texto visível.
 *
 * `<p><br></p>` é o que um campo de texto rico devolve vazio. Sem esta checagem,
 * um campo em branco contaria como preenchido na validação de obrigatoriedade.
 */
export function ehVazio(html: string): boolean {
  return textoVisivel(html) === '';
}

/**
 * Tags que separam blocos de texto.
 *
 * Abertura **e** fechamento contam: `Primeira<p>Segunda</p>` só tem tag de
 * fechamento no fim, e ignorar a de abertura colaria as duas linhas em
 * "PrimeiraSegunda" no resumo do histórico.
 */
const SEPARAM_BLOCO = /<\/?\s*(br|p|div|section|article|li|ul|ol|h[1-6]|blockquote|tr|td|th)\b[^>]*>/gi;

/**
 * O texto que sobra sem a marcação — para medir, resumir e comparar.
 *
 * Tag de bloco vira espaço; tag inline some sem deixar nada, senão
 * `<b>pul</b>sátil` viraria "pul sátil".
 */
export function textoVisivel(html: string): string {
  return html
    .replace(SEPARAM_BLOCO, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Converte texto puro em HTML de parágrafos — para o ditado e a colagem. */
export function textoParaHtml(texto: string): string {
  const paragrafos = texto
    .split(/\n{2,}/)
    .map((bloco) => bloco.trim())
    .filter((bloco) => bloco !== '');

  if (paragrafos.length === 0) return '';

  return paragrafos
    .map((bloco) => `<p>${escapar(bloco).split('\n').join('<br />')}</p>`)
    .join('');
}

function escapar(texto: string): string {
  return texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
